import { useState } from 'react';

import { requestApi } from 'api/Api';

import getVideoDuration from 'lib/helpers/getVideoDuration';

import { showAlert } from 'components/shared/Alert/Alert';

import { CHUNK_SIZE, FILE_UPLOAD_STATUS } from '../../GeneralUploader/components/lib/constants';

const useVideoUploader = ({
  initiateUrl = '/api/admin/videos/upload/initiate',
  presignPartUrl = '/api/admin/videos/upload/presign',
  completeUploadUrl = '/api/admin/videos/upload/complete'
} = {}) => {
  const [uploading, setUploading] = useState(false);

  const uploadFiles = async (files = [], params = {}, setUploadProgressList = () => {}) => {
    if (!files || files.length === 0) return;
    setUploading(true);

    for (const file of files) {
      const totalParts = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));

      setUploadProgressList((prev) => [
        ...prev.filter((p) => p.filename !== file.name),
        {
          filename: file.name,
          progress: 0,
          total: totalParts,
          size: file.size,
          status: FILE_UPLOAD_STATUS.INITIATED
        }
      ]);

      // read video duration (seconds) if file is a video
      let durationSeconds = 0;
      try {
        const dur = await getVideoDuration(file);
        durationSeconds = Math.round(dur) || 0;
      } catch (e) {
        durationSeconds = 0;
      }

      let info;
      try {
        const multipartRes = await requestApi({
          url: initiateUrl,
          params: {
            filename: file.name,
            contentType: file.type
          }
        });
        info = multipartRes?.data;
      } catch (err) {
        showAlert({ title: err ?? 'Failed to initialize multipart upload', color: 'danger' });
        setUploadProgressList((prev) =>
          prev.map((p) =>
            p.filename === file.name ? { ...p, status: FILE_UPLOAD_STATUS.ERROR } : p
          )
        );
        continue;
      }

      if (!info) {
        setUploadProgressList((prev) =>
          prev.map((p) =>
            p.filename === file.name ? { ...p, status: FILE_UPLOAD_STATUS.ERROR } : p
          )
        );
        continue;
      }

      const { uploadId, key } = info;
      const parts = [];
      let didPartUploadFail = false;

      for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
        const start = (partNumber - 1) * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const blob = file.slice(start, end);

        let signedUrl;
        try {
          const presignRes = await requestApi({
            url: presignPartUrl,
            params: {
              key,
              uploadId,
              partNumber
            }
          });
          signedUrl = presignRes?.data?.signedUrl;
        } catch (err) {
          showAlert({ title: err ?? 'Failed to presign part', color: 'danger' });
          setUploadProgressList((prev) =>
            prev.map((p) =>
              p.filename === file.name ? { ...p, status: FILE_UPLOAD_STATUS.ERROR } : p
            )
          );
          break;
        }

        if (!signedUrl) {
          setUploadProgressList((prev) =>
            prev.map((p) =>
              p.filename === file.name ? { ...p, status: FILE_UPLOAD_STATUS.ERROR } : p
            )
          );
          break;
        }

        try {
          const response = await fetch(signedUrl, {
            method: 'PUT',
            body: blob
          });

          if (!response.ok) {
            setUploadProgressList((prev) =>
              prev.map((p) =>
                p.filename === file.name ? { ...p, status: FILE_UPLOAD_STATUS.ERROR } : p
              )
            );
            break;
          }

          const percent = Math.ceil((partNumber / totalParts) * 100);
          setUploadProgressList((prev) =>
            prev.map((p) =>
              p.filename === file.name
                ? { ...p, progress: percent, status: FILE_UPLOAD_STATUS.UPLOADING }
                : p
            )
          );

          const etag = response.headers.get('ETag')?.replace(/"/g, '');
          if (etag) parts.push({ ETag: etag, PartNumber: partNumber });
          else throw new Error(`ETag not found for part ${partNumber}`);
        } catch (err) {
          didPartUploadFail = true;
          showAlert({ title: err?.message ?? 'Upload part failed', color: 'danger' });
          setUploadProgressList((prev) =>
            prev.map((p) =>
              p.filename === file.name ? { ...p, status: FILE_UPLOAD_STATUS.ERROR } : p
            )
          );
          break;
        }
      }

      if (parts.length > 0) {
        if (!didPartUploadFail && parts.length === totalParts) {
        try {
          await requestApi({
            url: completeUploadUrl,
            params: {
              key,
              uploadId,
              parts,
              duration_seconds: durationSeconds,
              title: file.name,
              ...params
            },
            onSuccess: () => {
              setUploadProgressList((prev) =>
                prev.map((p) =>
                  p.filename === file.name
                    ? { ...p, progress: 100, status: FILE_UPLOAD_STATUS.UPLOADED }
                    : p
                )
              );
              showAlert({ title: `${file.name} uploaded successfully`, color: 'success' });
            },
            onError: (error) => {
              showAlert({
                title: error ?? `Something went wrong completing upload`,
                color: 'danger'
              });
              setUploadProgressList((prev) =>
                prev.map((p) =>
                  p.filename === file.name ? { ...p, status: FILE_UPLOAD_STATUS.ERROR } : p
                )
              );
            }
          });
        } catch (err) {
          showAlert({ title: err?.message ?? 'Failed to complete upload', color: 'danger' });
          setUploadProgressList((prev) =>
            prev.map((p) =>
              p.filename === file.name ? { ...p, status: FILE_UPLOAD_STATUS.ERROR } : p
            )
          );
        }}
      }
    }

    setUploading(false);
  };

  return { uploading, uploadFiles };
};

export default useVideoUploader;
